import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Linkedin } from 'lucide-react';
import { companyInfo, navLinks } from '../data/content';

// TikTok icon component
const TikTok = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      <div className="section-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo-transparent.png" 
                alt="Apollo House Student Village" 
                className="h-20 w-auto"
              />
            </Link>
            <p className="text-slate-400 leading-relaxed">
              {companyInfo.tagline}. Quality student accommodation in Vanderbijlpark, NSFAS accredited.
            </p>
            <div className="flex gap-4">
              <a
                href={companyInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 bg-slate-800 hover:bg-coral rounded-lg flex items-center justify-center transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={companyInfo.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                className="w-10 h-10 bg-slate-800 hover:bg-coral rounded-lg flex items-center justify-center transition-colors"
              >
                <TikTok className="w-5 h-5" />
              </a>
              <a
                href={companyInfo.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="w-10 h-10 bg-slate-800 hover:bg-coral rounded-lg flex items-center justify-center transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 font-['Outfit'] text-coral">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <Link
                    to={link.href}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-slate-400 hover:text-coral transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="font-bold text-lg mb-6 font-['Outfit'] text-coral">Our Locations</h4>
            <ul className="space-y-4">
              {companyInfo.addresses.map((address, idx) => (
                <li key={idx} className="flex gap-3">
                  <MapPin className="w-5 h-5 text-coral shrink-0 mt-0.5" />
                  <div className="text-slate-400">
                    <p className="font-medium text-white">{address.name}</p>
                    <p>{address.street}</p>
                    <p>{address.city}, {address.postalCode}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6 font-['Outfit'] text-coral">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${companyInfo.phone.tel}`}
                  data-testid="footer-tel"
                  className="flex items-center gap-3 text-slate-400 hover:text-coral transition-colors"
                >
                  <Phone className="w-5 h-5 text-coral" />
                  <span>{companyInfo.phone.tel}</span>
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${companyInfo.phone.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="flex items-center gap-3 text-slate-400 hover:text-coral transition-colors"
                >
                  <Phone className="w-5 h-5 text-coral" />
                  <span>WhatsApp: {companyInfo.phone.whatsappDisplay}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.emails.general}`}
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-slate-400 hover:text-coral transition-colors"
                >
                  <Mail className="w-5 h-5 text-coral" />
                  <span>{companyInfo.emails.general}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} Apollo House. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-coral transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-coral transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
